import { useTranslation } from 'react-i18next';

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { BatchStatusBadge, type BatchStatus } from '@/components/efs/BatchStatusBadge';
import { useCurrentUser } from '@/lib/auth-context';
import { canAccessPath } from '@/lib/role-access';
import { blockerCategoryKeys, isBlockerCategory } from '@/features/audit-issues';

import type { BlockerBatch } from './types';

export interface BlockerCategoryCardProps {
  category: string;
  count: number;
  batches: BlockerBatch[];
  /** Resolved destination for one sampled batch (allowlisted path). */
  pathForBatch: (batch: BlockerBatch) => string;
  onOpenBatch: (batch: BlockerBatch) => void;
  /** Filtered full-list destination when the sample is capped. */
  onOpenList: () => void;
}

/**
 * One blocker-dashboard category card (spec-13 §8.3): localized title,
 * explanation, next action, human-readable sampled batches with
 * permission-aware navigation, and the capped-sample notice. Shared between
 * `BlockerDashboardPage` and the DEV-only tooling so the rendered
 * markup can never drift from production.
 */
export const BlockerCategoryCard = ({
  category,
  count,
  batches,
  pathForBatch,
  onOpenBatch,
  onOpenList,
}: BlockerCategoryCardProps) => {
  const { t } = useTranslation(['issues', 'common']);
  const user = useCurrentUser();

  const isCataloged = isBlockerCategory(category);
  const keys = isCataloged ? blockerCategoryKeys(category) : null;
  const title = keys ? t(keys.title) : t('efs.issues.generic.title', { ns: 'issues' });
  const message = keys ? t(keys.message) : t('efs.issues.generic.message', { ns: 'issues' });
  const action = keys ? t(keys.action) : null;
  const hasBatches = batches.length > 0;
  const isCapped = count > batches.length;

  return (
    <Card className={count === 0 ? 'opacity-60' : undefined}>
      <CardHeader className="space-y-1 pb-2">
        <CardTitle className="flex items-center justify-between gap-2 text-sm">
          <span>{title}</span>
          <span
            className={`shrink-0 rounded-full px-2 py-0.5 text-xs font-semibold ${
              count > 0 ? 'bg-destructive text-white' : 'bg-efs-bg-subtle text-efs-text-muted'
            }`}
          >
            {count}
          </span>
        </CardTitle>
        {/* Explanation + next action (spec-13 §8.3/§7.1) — never title-only. */}
        <p className="text-xs text-efs-text-muted">{message}</p>
        {action ? <p className="text-xs font-medium text-efs-text-primary">{action}</p> : null}
      </CardHeader>
      {hasBatches || isCapped ? (
        <CardContent className="space-y-1 pt-0">
          {batches.map((batch) => {
            const path = pathForBatch(batch);
            const allowed = canAccessPath(user, path.split('?')[0]);
            const label = batch.facility_name
              ? `${batch.year_month} / ${batch.facility_name}`
              : batch.year_month;
            if (!allowed) {
              // spec-13 §11: never route predictably into a forbidden page —
              // show a non-interactive row with a muted hint instead.
              return (
                <div
                  key={batch.monthly_batch_id}
                  className="flex w-full items-center justify-between gap-2 rounded bg-efs-bg-subtle px-2 py-1 text-xs text-efs-text-muted"
                >
                  <span className="truncate">{label}</span>
                  <span className="shrink-0">
                    {t('efs.issues.target.no_permission', { ns: 'issues' })}
                  </span>
                </div>
              );
            }
            return (
              <button
                key={batch.monthly_batch_id}
                type="button"
                onClick={() => onOpenBatch(batch)}
                className="flex w-full items-center justify-between gap-2 rounded bg-efs-bg-subtle px-2 py-1 text-left text-xs text-efs-text-primary hover:bg-efs-border/60"
              >
                <span className="truncate">{label}</span>
                <BatchStatusBadge status={batch.status as BatchStatus} className="shrink-0" />
              </button>
            );
          })}
          {isCapped ? (
            <div className="flex items-center justify-between gap-2 pt-1 text-[11px] text-efs-text-muted">
              <span>
                {t('efs.issues.blockers.shown_limited', { count: batches.length, ns: 'issues' })}
              </span>
              <button
                type="button"
                className="text-efs-accent hover:underline"
                onClick={onOpenList}
              >
                {t('efs.issues.blockers.open_list', { ns: 'issues' })}
              </button>
            </div>
          ) : null}
        </CardContent>
      ) : null}
    </Card>
  );
};
